import { createTable } from "robodux";
import { createSelector } from "reselect";
import { put, select } from "saga-query";
import { api, thunks } from "@app/state/apis/api";
import { appNotify } from "./notify";
import type { TDBShowCase, TDBRunShowCase } from "@app/types";
import type { RootState } from "@app/state/rootState";

const REPO_SHOWCASES = "showcases";

const mapShowCases = (
  showcases: TDBShowCase[]
): Record<string, TDBRunShowCase> =>
  showcases.reduce(
    (acc: Record<TDBShowCase["SC_UUID"], TDBRunShowCase>, showcase) => {
      acc[showcase.SC_UUID] = {
        ...showcase,
        selectionOptions: showcase.SELOPTIONS
          ? JSON.parse(showcase.SELOPTIONS)
          : null,
        runtimeParams: {},
      };
      return acc;
    },
    {}
  );

export const repoShowCases = createTable<TDBRunShowCase>({
  name: REPO_SHOWCASES,
  initialState: {},
});

const selectors = repoShowCases.getSelectors(
  (state: RootState) => state[REPO_SHOWCASES]
);

export const selectAllShowCaseList = createSelector(
  selectors.selectTable,
  (t) => t
);
export const selectAllShowCaseArr = createSelector(
  selectors.selectTableAsList,
  (t) => t
);

export const selectShowCaseById = (state: RootState, id: string | number) =>
  selectors.selectById(state, { id: `${id}` }) || ({} as TDBRunShowCase);

export const selectRuntimeParamsById = (state: RootState, id: string) => {
  if (!id) return null;
  if (state === undefined) return null;
  const sc = selectors.selectById(state, { id: `${id}` }) as TDBRunShowCase;
  return sc?.runtimeParams || null;
};

export const showcaseInitialize = thunks.create<string>(
  "showcaseInitialize",
  function* (ctx, next) {
    yield* put(getShowCases());
  }
);

export const getShowCases = api.get("/moa-registered", function* (ctx, next) {
  ctx.cache = true;
  ctx.request = ctx.req({
    url: "/showcase/u-registered-reports",
    headers: {
      credentials: "include",
    },
  });
  yield* next();
  if (!ctx.json.ok) {
    //error shown by api middleware //
    return;
  }
  const dataset = mapShowCases(ctx.json.data);
  yield* put(repoShowCases.actions.set(dataset));
});

type TRuntimeParams = {
  id: string;
  runtimeParams: any;
};
export const runtimeParamsState = thunks.create<TRuntimeParams>(
  "runtimeParamsState",
  function* (ctx, next) {
    const { id } = ctx.payload;
    const sc = yield* select((s: RootState) => selectShowCaseById(s, id));
    if (!sc) {
      yield* put(
        appNotify({
          type: "error",
          message: "Showcase not found",
          duration: 5000,
          position: "bottom",
        })
      );
      return;
    }
    yield* put(
      repoShowCases.actions.patch({
        [id]: { ...sc, runtimeParams: ctx.payload.runtimeParams },
      })
    );

    yield* next();
  }
);
